/**
 * Which "connect" a background preload belongs to.
 *
 * The Object Explorer's preload outlives the thing that started it: a wave can
 * still be fanning out (2 databases at a time on pgsql/mssql/mysql, five
 * listings each) when the user disconnects and reconnects the same connection
 * id. Its final `setPreloadedData` then publishes the OLD database list with
 * `isPreloading: false` on top of the NEW connection's data — readers get a
 * stale list presented as complete, while the live work is still pending.
 *
 * Queue retention cannot fix that: making the reconnect wait behind an obsolete
 * wave only delays the live work, and the stale publish still lands. So every
 * preload captures the generation it was born in, and every late write checks
 * it. A disconnect / removal / reconnect-in-place bumps the number and the old
 * work goes quiet by itself.
 *
 * Generations are never deleted: a connection id whose counter was dropped
 * would restart at 1, and a captured generation 1 from the dead connect would
 * read as live again. One integer per id ever seen is not a leak worth that.
 */

export interface ConnectionGenerations {
  /** The live generation for a connection. Starts at 1 and only grows. */
  current: (connectionId: string) => number;
  /** Ends the current generation. Everything captured before this is stale. */
  bump: (connectionId: string) => number;
  isCurrent: (connectionId: string, generation: number) => boolean;
}

export function createConnectionGenerations(): ConnectionGenerations {
  const generations = new Map<string, number>();

  const current = (connectionId: string): number => generations.get(connectionId) ?? 1;

  return {
    current,
    bump: (connectionId) => {
      const next = current(connectionId) + 1;
      generations.set(connectionId, next);
      return next;
    },
    isCurrent: (connectionId, generation) => current(connectionId) === generation,
  };
}
